import type { editor as MonacoEditor } from "monaco-editor"
import {
  getTikzShape,
  tikzShapeSnippet,
} from "@/lib/ribbon/tikz-shapes"
import {
  unwrapEquationDraft,
  wrapEquationForDocument,
} from "@/lib/ribbon/equation-symbols"

type Ed = MonacoEditor.IStandaloneCodeEditor

/* ── Ribbon insert highlight (body/preamble code; not \\usepackage) ───────── */

const RIBBON_HIGHLIGHT_CLASS = "ribbon-insert-highlight"
const RIBBON_HIGHLIGHT_MS = 1600
const RIBBON_HIGHLIGHT_CSS = `
@keyframes ribbon-insert-shimmer {
  0% {
    background-color: rgba(14, 165, 233, 0.5);
    box-shadow: inset 0 0 0 1px rgba(14, 165, 233, 0.35);
  }
  40% {
    background-color: rgba(56, 189, 248, 0.35);
  }
  100% {
    background-color: rgba(14, 165, 233, 0);
    box-shadow: inset 0 0 0 1px rgba(14, 165, 233, 0);
  }
}
.monaco-editor .${RIBBON_HIGHLIGHT_CLASS} {
  background-color: rgba(14, 165, 233, 0.4);
  border-radius: 2px;
  animation: ribbon-insert-shimmer ${RIBBON_HIGHLIGHT_MS}ms ease-out forwards;
}
.monaco-editor.vs-dark .${RIBBON_HIGHLIGHT_CLASS},
.monaco-editor .${RIBBON_HIGHLIGHT_CLASS}-dark {
  background-color: rgba(56, 189, 248, 0.35);
  animation-name: ribbon-insert-shimmer-dark;
}
@keyframes ribbon-insert-shimmer-dark {
  0% { background-color: rgba(56, 189, 248, 0.55); }
  40% { background-color: rgba(56, 189, 248, 0.3); }
  100% { background-color: rgba(56, 189, 248, 0); }
}
`

let ribbonHighlightStyleInjected = false
const ribbonHighlightDecorationIds = new WeakMap<Ed, string[]>()
const ribbonHighlightTimers = new WeakMap<Ed, number>()

function ensureRibbonHighlightStyles(): void {
  if (ribbonHighlightStyleInjected) return
  if (typeof document === "undefined") return
  const style = document.createElement("style")
  style.setAttribute("data-ribbon-insert-highlight", "true")
  style.textContent = RIBBON_HIGHLIGHT_CSS
  document.head.appendChild(style)
  ribbonHighlightStyleInjected = true
}

/** Soft shimmer on text just inserted by a ribbon action (not package imports). */
function highlightRibbonInsert(
  ed: Ed,
  startOffset: number,
  endOffset: number,
): void {
  if (endOffset <= startOffset) return
  const model = ed.getModel()
  if (!model) return

  ensureRibbonHighlightStyles()

  const start = model.getPositionAt(startOffset)
  const end = model.getPositionAt(endOffset)
  const range = {
    startLineNumber: start.lineNumber,
    startColumn: start.column,
    endLineNumber: end.lineNumber,
    endColumn: end.column,
  }

  const prev = ribbonHighlightDecorationIds.get(ed) ?? []
  const next = ed.deltaDecorations(prev, [
    {
      range,
      options: {
        inlineClassName: RIBBON_HIGHLIGHT_CLASS,
        stickiness: 1, // NeverGrowsWhenTypingAtEdges
      },
    },
  ])
  ribbonHighlightDecorationIds.set(ed, next)

  try {
    ed.revealRangeInCenterIfOutsideViewport(range)
  } catch {
    // ignore if range is invalid mid-edit
  }

  const existingTimer = ribbonHighlightTimers.get(ed)
  if (existingTimer != null) window.clearTimeout(existingTimer)
  const timer = window.setTimeout(() => {
    const ids = ribbonHighlightDecorationIds.get(ed)
    if (ids === next) {
      ribbonHighlightDecorationIds.set(ed, ed.deltaDecorations(ids, []))
    }
    ribbonHighlightTimers.delete(ed)
  }, RIBBON_HIGHLIGHT_MS)
  ribbonHighlightTimers.set(ed, timer)
}

function rangeStartOffset(
  model: MonacoEditor.ITextModel,
  range: MonacoEditor.IRange,
): number {
  const a = model.getOffsetAt({
    lineNumber: range.startLineNumber,
    column: range.startColumn,
  })
  const b = model.getOffsetAt({
    lineNumber: range.endLineNumber,
    column: range.endColumn,
  })
  return Math.min(a, b)
}

/** Body edit from the ribbon — applies text and highlights the inserted span. */
function applyRibbonTextEdit(
  ed: Ed,
  range: MonacoEditor.IRange,
  text: string,
): void {
  const model = ed.getModel()
  if (!model) return
  const startOffset = rangeStartOffset(model, range)
  ed.executeEdits("ribbon", [
    { range, text, forceMoveMarkers: true },
  ])
  highlightRibbonInsert(ed, startOffset, startOffset + text.length)
}

function snippetToPlain(snippet: string): string {
  return snippet.replace(/\$\{\d+:?([^}]*)\}/g, "$1").replace(/\$\d+/g, "")
}

function getSelectionText(ed: Ed): string {
  const model = ed.getModel()
  const sel = ed.getSelection()
  if (!model || !sel) return ""
  return model.getValueInRange(sel)
}

/**
 * Ensure `\usepackage{name}` (or `\usepackage[...]{name}`) exists in the preamble.
 * Inserts once after `\documentclass{...}` if missing; no-op if already present.
 */
export function ensurePackage(ed: Ed, packageName: string, options?: string): void {
  const model = ed.getModel()
  if (!model) return

  const full = model.getValue()
  // Match \usepackage{name} or \usepackage[...]{name} (also xcolor loaded via other pkgs less common)
  const already =
    new RegExp(
      String.raw`\\usepackage\s*(?:\[[^\]]*\])?\s*\{[^}]*\b${packageName}\b[^}]*\}`,
    ).test(full) ||
    // Common alias: \RequirePackage{xcolor}
    new RegExp(
      String.raw`\\RequirePackage\s*(?:\[[^\]]*\])?\s*\{[^}]*\b${packageName}\b[^}]*\}`,
    ).test(full)

  if (already) return

  const pkgLine = options
    ? `\\usepackage[${options}]{${packageName}}\n`
    : `\\usepackage{${packageName}}\n`

  // Prefer insert right after \documentclass[...]{...}
  const docClass = /\\documentclass\s*(?:\[[^\]]*\])?\s*\{[^}]+\}/
  const docMatch = docClass.exec(full)
  if (docMatch && docMatch.index != null) {
    const insertOffset = docMatch.index + docMatch[0].length
    // Skip trailing newline(s) so we land on the next line
    let at = insertOffset
    if (full[at] === "\r") at++
    if (full[at] === "\n") at++
    const pos = model.getPositionAt(at)
    ed.executeEdits("ribbon-usepackage", [
      {
        range: {
          startLineNumber: pos.lineNumber,
          startColumn: pos.column,
          endLineNumber: pos.lineNumber,
          endColumn: pos.column,
        },
        text: pkgLine,
        forceMoveMarkers: true,
      },
    ])
    return
  }

  // Else insert before \begin{document}
  const beginDoc = /\\begin\s*\{\s*document\s*\}/
  const beginMatch = beginDoc.exec(full)
  if (beginMatch && beginMatch.index != null) {
    const pos = model.getPositionAt(beginMatch.index)
    ed.executeEdits("ribbon-usepackage", [
      {
        range: {
          startLineNumber: pos.lineNumber,
          startColumn: pos.column,
          endLineNumber: pos.lineNumber,
          endColumn: pos.column,
        },
        text: pkgLine,
        forceMoveMarkers: true,
      },
    ])
    return
  }

  // Last resort: top of file
  ed.executeEdits("ribbon-usepackage", [
    {
      range: {
        startLineNumber: 1,
        startColumn: 1,
        endLineNumber: 1,
        endColumn: 1,
      },
      text: pkgLine,
      forceMoveMarkers: true,
    },
  ])
}

/**
 * Ensure `\usetikzlibrary{name}` exists in the preamble (after `\usepackage{tikz}` when possible).
 */
export function ensureTikzLibrary(ed: Ed, libraryName: string): void {
  ensurePackage(ed, "tikz")
  const model = ed.getModel()
  if (!model) return

  const full = model.getValue()
  const escaped = libraryName.replace(/\./g, "\\.")
  const already = new RegExp(
    String.raw`\\usetikzlibrary\s*\{[^}]*\b${escaped}\b[^}]*\}`,
  ).test(full)
  if (already) return

  const libLine = `\\usetikzlibrary{${libraryName}}\n`

  const tikzPkg = /\\usepackage\s*(?:\[[^\]]*\])?\s*\{[^}]*\btikz\b[^}]*\}/.exec(full)
  if (tikzPkg && tikzPkg.index != null) {
    let at = tikzPkg.index + tikzPkg[0].length
    if (full[at] === "\r") at++
    if (full[at] === "\n") at++
    const pos = model.getPositionAt(at)
    ed.executeEdits("ribbon-tikzlibrary", [
      {
        range: {
          startLineNumber: pos.lineNumber,
          startColumn: pos.column,
          endLineNumber: pos.lineNumber,
          endColumn: pos.column,
        },
        text: libLine,
        forceMoveMarkers: true,
      },
    ])
    return
  }

  const beginMatch = /\\begin\s*\{\s*document\s*\}/.exec(full)
  if (beginMatch && beginMatch.index != null) {
    const pos = model.getPositionAt(beginMatch.index)
    ed.executeEdits("ribbon-tikzlibrary", [
      {
        range: {
          startLineNumber: pos.lineNumber,
          startColumn: pos.column,
          endLineNumber: pos.lineNumber,
          endColumn: pos.column,
        },
        text: libLine,
        forceMoveMarkers: true,
      },
    ])
  }
}

/** Insert a Monaco snippet at the current selection (replacing it). */
export function insertSnippet(ed: Ed, snippet: string): void {
  ed.focus()
  const sel = ed.getSelection()
  const model = ed.getModel()
  if (!sel || !model) return

  const startOffset = rangeStartOffset(model, sel)
  const plain = snippetToPlain(snippet)

  // SnippetController2 is the public contribution id used by Monaco
  const contribution = ed.getContribution("snippetController2") as
    | { insert: (template: string) => void }
    | null
  if (contribution?.insert) {
    if (!sel.isEmpty()) {
      ed.executeEdits("ribbon", [
        { range: sel, text: "", forceMoveMarkers: true },
      ])
    }
    // Monaco/TextMate treats `\` as an escape character. Callers pass normal
    // LaTeX (e.g. `\begin`, `\\` row breaks); double every `\` so those survive
    // as `\begin` / `\\` in the buffer instead of being eaten (`\\` → `\`).
    contribution.insert(snippet.replace(/\\/g, "\\\\"))
    highlightRibbonInsert(ed, startOffset, startOffset + plain.length)
    return
  }
  // Fallback: plain text without tabstops
  applyRibbonTextEdit(ed, sel, plain)
  ed.focus()
}

/** Wrap selection with before/after; empty selection inserts snippet with placeholder. */
export function wrapSelection(
  ed: Ed,
  before: string,
  after: string,
  placeholder = "text",
): void {
  ed.focus()
  const selected = getSelectionText(ed)
  if (selected) {
    const snippet = `${before}${selected.replace(/\$/g, "\\$")}${after}`
    const sel = ed.getSelection()
    if (!sel) return
    applyRibbonTextEdit(ed, sel, snippet)
    // Place cursor after inserted text
    const model = ed.getModel()
    if (model) {
      const start = sel.getStartPosition()
      const endOffset =
        model.getOffsetAt(start) + before.length + selected.length + after.length
      const endPos = model.getPositionAt(endOffset)
      ed.setPosition(endPos)
    }
    ed.focus()
    return
  }
  insertSnippet(ed, `${before}\${1:${placeholder}}${after}`)
}

export function wrapEnvironment(ed: Ed, envName: string, options?: string): void {
  const opt = options ? `[${options}]` : ""
  const selected = getSelectionText(ed)
  const sel = ed.getSelection()
  if (!sel) return
  if (selected) {
    const body = selected
      .split("\n")
      .map((line) => (line.length ? `  ${line}` : line))
      .join("\n")
    const text = `\\begin{${envName}}${opt}\n${body}\n\\end{${envName}}`
    applyRibbonTextEdit(ed, sel, text)
    ed.focus()
    return
  }
  insertSnippet(
    ed,
    `\\begin{${envName}}${opt}\n  \${1:content}\n\\end{${envName}}`,
  )
}

export function insertList(
  ed: Ed,
  kind: "itemize" | "enumerate" | "alpha",
): void {
  const env =
    kind === "alpha" ? "enumerate" : kind
  const opt = kind === "alpha" ? "label=\\alph*" : undefined
  if (kind === "alpha") {
    ensurePackage(ed, "enumitem")
  }
  const selected = getSelectionText(ed)
  if (selected.trim()) {
    const lines = selected.split("\n").filter((l) => l.trim().length > 0)
    const items = lines.map((l) => `  \\item ${l.trim()}`).join("\n")
    const optStr = opt ? `[${opt}]` : ""
    const text = `\\begin{${env}}${optStr}\n${items}\n\\end{${env}}`
    const sel = ed.getSelection()
    if (!sel) return
    applyRibbonTextEdit(ed, sel, text)
    ed.focus()
    return
  }
  const optStr = opt ? `[${opt}]` : ""
  insertSnippet(
    ed,
    `\\begin{${env}}${optStr}\n  \\item \${1:item}\n  \\item \${2:item}\n\\end{${env}}`,
  )
}

export async function clipboardAction(
  ed: Ed,
  action: "cut" | "copy" | "paste",
): Promise<void> {
  ed.focus()

  if (action === "paste") {
    await pasteFromClipboard(ed)
    return
  }

  const selected = getSelectionText(ed)
  if (action === "copy") {
    if (selected) {
      try {
        await navigator.clipboard.writeText(selected)
      } catch {
        ed.trigger("ribbon", "editor.action.clipboardCopyAction", null)
      }
    }
    return
  }

  // cut
  if (selected) {
    try {
      await navigator.clipboard.writeText(selected)
      const sel = ed.getSelection()
      if (sel) {
        ed.executeEdits("ribbon", [{ range: sel, text: "", forceMoveMarkers: true }])
      }
    } catch {
      ed.trigger("ribbon", "editor.action.clipboardCutAction", null)
    }
    return
  }
  ed.trigger("ribbon", "editor.action.clipboardCutAction", null)
}

async function pasteFromClipboard(ed: Ed): Promise<void> {
  ed.focus()
  let text = ""
  try {
    text = await navigator.clipboard.readText()
  } catch {
    // Fallback: Monaco's built-in action (often blocked by browser security)
    ed.trigger("ribbon", "editor.action.clipboardPasteAction", null)
    return
  }
  if (!text) return

  const sel = ed.getSelection()
  if (!sel) return

  ed.pushUndoStop()
  ed.executeEdits("ribbon-paste", [
    { range: sel, text, forceMoveMarkers: true },
  ])
  ed.pushUndoStop()

  // Place cursor after pasted text
  const model = ed.getModel()
  if (model) {
    const start = sel.getStartPosition()
    const endPos = model.getPositionAt(model.getOffsetAt(start) + text.length)
    ed.setPosition(endPos)
    ed.setSelection({
      startLineNumber: endPos.lineNumber,
      startColumn: endPos.column,
      endLineNumber: endPos.lineNumber,
      endColumn: endPos.column,
    })
  }
  ed.focus()
}

export function undo(ed: Ed): void {
  ed.focus()
  const action = ed.getAction("undo")
  if (action) {
    void action.run()
    return
  }
  ed.trigger("keyboard", "undo", null)
}

export function redo(ed: Ed): void {
  ed.focus()
  const action = ed.getAction("redo")
  if (action) {
    void action.run()
    return
  }
  ed.trigger("keyboard", "redo", null)
}

/**
 * Find `query` in the active document. Empty query opens Monaco's Find widget.
 * Repeated Enter advances to the next match; direction "prev" goes backward.
 */
export function findInDocument(
  ed: Ed,
  query: string,
  direction: "next" | "prev" = "next",
): void {
  ed.focus()
  const q = query.trim()
  if (!q) {
    void ed.getAction("actions.find")?.run()
    return
  }

  const model = ed.getModel()
  if (!model) return

  const matches = model.findMatches(q, false, false, false, null, true)
  if (matches.length === 0) return

  const sel = ed.getSelection()
  let idx = 0

  if (sel) {
    const curIdx = matches.findIndex(
      (m) =>
        m.range.startLineNumber === sel.startLineNumber &&
        m.range.startColumn === sel.startColumn &&
        m.range.endLineNumber === sel.endLineNumber &&
        m.range.endColumn === sel.endColumn,
    )

    if (curIdx >= 0) {
      idx =
        direction === "next"
          ? (curIdx + 1) % matches.length
          : (curIdx - 1 + matches.length) % matches.length
    } else {
      const pos = sel.getStartPosition()
      const after = matches.findIndex((m) => {
        const start = m.range.getStartPosition()
        return (
          start.lineNumber > pos.lineNumber ||
          (start.lineNumber === pos.lineNumber && start.column >= pos.column)
        )
      })
      if (direction === "next") {
        idx = after >= 0 ? after : 0
      } else {
        idx = after > 0 ? after - 1 : matches.length - 1
      }
    }
  }

  const match = matches[idx]
  ed.setSelection(match.range)
  ed.revealRangeInCenter(match.range)
}

export function applyBold(ed: Ed): void {
  wrapSelection(ed, "\\textbf{", "}")
}

export function applyItalic(ed: Ed): void {
  wrapSelection(ed, "\\textit{", "}")
}

export function applyUnderline(ed: Ed): void {
  wrapSelection(ed, "\\underline{", "}")
}

export function applyAlign(
  ed: Ed,
  align: "left" | "center" | "right" | "justify",
): void {
  const env =
    align === "left"
      ? "flushleft"
      : align === "center"
        ? "center"
        : align === "right"
          ? "flushright"
          : "justify"
  // `justify` comes from ragged2e; built-in envs need no package
  if (env === "justify") ensurePackage(ed, "ragged2e")
  wrapEnvironment(ed, env)
}

export function applyFontFamily(ed: Ed, fontName: string): void {
  // Same pattern as bold/color/font-size: wrap selection (or insert a placeholder
  // snippet). Also ensure fontspec so compilation doesn't fail.
  ensurePackage(ed, "fontspec")
  wrapSelection(ed, `{\\fontspec{${fontName}} `, "}", "text")
}

export function applyFontSize(ed: Ed, sizePt: number): void {
  const baseline = Math.round(sizePt * 1.2)
  const selected = getSelectionText(ed)
  if (selected) {
    wrapSelection(
      ed,
      `{\\fontsize{${sizePt}}{${baseline}}\\selectfont `,
      "}",
      "text",
    )
    return
  }
  insertSnippet(
    ed,
    `{\\fontsize{${sizePt}}{${baseline}}\\selectfont \${1:text}}`,
  )
}

export function applyFillColor(ed: Ed, color: string): void {
  ensurePackage(ed, "xcolor")
  const hex = hexToXcolor(color)
  wrapSelection(ed, `\\colorbox[HTML]{${hex}}{`, "}")
}

export function applyTextColor(ed: Ed, color: string): void {
  ensurePackage(ed, "xcolor")
  const hex = hexToXcolor(color)
  wrapSelection(ed, `\\textcolor[HTML]{${hex}}{`, "}")
}

/** Left indent step used by Increase/Decrease Indent (PDF-visible via adjustwidth). */
const INDENT_LEFT = "2em"
const INDENT_RIGHT = "0pt"
const ADJUSTWIDTH_BEGIN = `\\begin{adjustwidth}{${INDENT_LEFT}}{${INDENT_RIGHT}}`
const ADJUSTWIDTH_END = "\\end{adjustwidth}"

function getSelectedLinesRange(ed: Ed): MonacoEditor.IRange | null {
  const model = ed.getModel()
  const sel = ed.getSelection()
  if (!model || !sel) return null
  const start = Math.min(sel.startLineNumber, sel.endLineNumber)
  const end = Math.max(sel.startLineNumber, sel.endLineNumber)
  return {
    startLineNumber: start,
    startColumn: 1,
    endLineNumber: end,
    endColumn: model.getLineMaxColumn(end),
  }
}

function stripOneSourceIndent(line: string): string {
  // Prefer stripping LaTeX indent commands that affect the PDF
  const cmd =
    /^(?:\\hspace\*\{[^}]+\}\s*|\\hspace\{[^}]+\}\s*|\\qquad\s*|\\quad\s*)/.exec(
      line,
    )
  if (cmd) return line.slice(cmd[0].length)
  // Fall back: editor whitespace only (no PDF effect, but cleans prior toolbar misuse)
  if (line.startsWith("\t")) return line.slice(1)
  if (line.startsWith("  ")) return line.slice(2)
  if (line.startsWith(" ")) return line.slice(1)
  return line
}

/**
 * Increase paragraph indent in the PDF by wrapping the selected lines in
 * `adjustwidth` (requires changepage). Nested calls stack another level.
 */
export function increaseIndent(ed: Ed): void {
  ensurePackage(ed, "changepage")
  ed.focus()
  const model = ed.getModel()
  const range = getSelectedLinesRange(ed)
  if (!model || !range) return

  const selected = model.getValueInRange(range)
  if (!selected.trim()) {
    insertSnippet(
      ed,
      `${ADJUSTWIDTH_BEGIN}\n  \${1:text}\n${ADJUSTWIDTH_END}`,
    )
    return
  }

  const body = selected
    .split("\n")
    .map((line) => (line.length ? `  ${line}` : line))
    .join("\n")
  const text = `${ADJUSTWIDTH_BEGIN}\n${body}\n${ADJUSTWIDTH_END}`
  applyRibbonTextEdit(ed, range, text)
  ed.focus()
}

type AdjustwidthBlock = {
  start: number
  end: number
  bodyStart: number
  bodyEnd: number
  depth: number
}

/**
 * Find all balanced `adjustwidth` environments in `full`, with nesting depth.
 * Does not depend on the current text selection containing `\begin`/`\end`.
 */
function findAdjustwidthBlocks(full: string): AdjustwidthBlock[] {
  const blocks: AdjustwidthBlock[] = []
  // Allow optional whitespace: \begin{adjustwidth}{2em}{0pt} / \end{adjustwidth}
  const tokenRe =
    /\\begin\s*\{adjustwidth\}\s*\{[^}]*\}\s*\{[^}]*\}|\\end\s*\{adjustwidth\}/g
  const stack: { start: number; bodyStart: number; depth: number }[] = []
  let tm: RegExpExecArray | null
  while ((tm = tokenRe.exec(full)) !== null) {
    const token = tm[0]
    if (token.startsWith("\\begin")) {
      let bodyStart = tm.index + token.length
      if (full[bodyStart] === "\r") bodyStart++
      if (full[bodyStart] === "\n") bodyStart++
      stack.push({ start: tm.index, bodyStart, depth: stack.length + 1 })
    } else if (stack.length) {
      const open = stack.pop()!
      blocks.push({
        start: open.start,
        end: tm.index + token.length,
        bodyStart: open.bodyStart,
        bodyEnd: tm.index,
        depth: open.depth,
      })
    }
  }
  return blocks
}

/** Innermost adjustwidth that fully contains [from, to] (offsets into the doc). */
function findInnermostAdjustwidth(
  full: string,
  from: number,
  to: number,
): AdjustwidthBlock | null {
  const lo = Math.min(from, to)
  const hi = Math.max(from, to)
  const containing = findAdjustwidthBlocks(full).filter(
    (b) => lo >= b.start && hi <= b.end,
  )
  if (!containing.length) return null
  // Highest depth = innermost
  return containing.reduce((a, b) => (b.depth >= a.depth ? b : a))
}

function unwrapAdjustwidthBody(full: string, block: AdjustwidthBlock): string {
  let bodyEnd = block.bodyEnd
  if (full[bodyEnd - 1] === "\n") bodyEnd--
  if (full[bodyEnd - 1] === "\r") bodyEnd--
  const body = full.slice(block.bodyStart, bodyEnd)
  // Remove the two-space source indent that increaseIndent adds per line
  return body
    .split("\n")
    .map((line) =>
      line.startsWith("  ") ? line.slice(2) : line.replace(/^\t/, ""),
    )
    .join("\n")
}

/**
 * Decrease PDF indent by unwrapping the innermost `adjustwidth` that surrounds
 * the cursor/selection (begin/end do not need to be selected). Falls back to
 * stripping a leading \\hspace / \\quad / source spaces when none surrounds.
 */
export function decreaseIndent(ed: Ed): void {
  ed.focus()
  const model = ed.getModel()
  const sel = ed.getSelection()
  if (!model || !sel) return

  const full = model.getValue()
  // Prefer the primary cursor; also cover the selection span so a partial
  // selection inside an env still resolves to that surrounding env.
  const anchor = ed.getPosition() ?? sel.getStartPosition()
  const selStart = model.getOffsetAt(sel.getStartPosition())
  const selEnd = model.getOffsetAt(sel.getEndPosition())
  const cursor = model.getOffsetAt(anchor)

  // 1) Surrounding-environment based (primary): inspect the whole document
  const block =
    findInnermostAdjustwidth(full, selStart, selEnd) ??
    findInnermostAdjustwidth(full, cursor, cursor)

  if (block) {
    const text = unwrapAdjustwidthBody(full, block)
    const startPos = model.getPositionAt(block.start)
    const endPos = model.getPositionAt(block.end)
    applyRibbonTextEdit(
      ed,
      {
        startLineNumber: startPos.lineNumber,
        startColumn: startPos.column,
        endLineNumber: endPos.lineNumber,
        endColumn: endPos.column,
      },
      text,
    )
    // Place cursor at the start of the unwrapped content
    ed.setPosition(startPos)
    ed.focus()
    return
  }

  // 2) Fallback: no surrounding adjustwidth — strip one source/LaTeX indent
  const lineRange = getSelectedLinesRange(ed)
  if (!lineRange) return
  const selected = model.getValueInRange(lineRange)
  const next = selected.split("\n").map(stripOneSourceIndent).join("\n")
  if (next === selected) return
  applyRibbonTextEdit(ed, lineRange, next)
  ed.focus()
}

export function insertPageBreak(ed: Ed): void {
  insertSnippet(ed, "\\newpage\n")
}

export function insertPicture(ed: Ed): void {
  insertSnippet(
    ed,
    "\\includegraphics[width=\\linewidth]{${1:path/to/image}}",
  )
}

export function insertShapes(ed: Ed, shapeId = "rectangle"): void {
  const shape = getTikzShape(shapeId) ?? getTikzShape("rectangle")
  if (!shape) return

  ensurePackage(ed, "tikz")
  for (const lib of shape.libraries ?? []) {
    ensureTikzLibrary(ed, lib)
  }
  insertSnippet(ed, tikzShapeSnippet(shape))
}

/**
 * Insert an `equation` environment.
 * When `body` is provided (Equation Editor popup), wrap that LaTeX;
 * otherwise insert an empty environment with a snippet placeholder.
 */
export function insertEquation(ed: Ed, body?: string): void {
  const trimmed = body?.trim()
  if (!trimmed) {
    wrapEnvironment(ed, "equation")
    return
  }

  ensurePackage(ed, "amsmath")
  ensurePackage(ed, "amssymb")

  const sel = ed.getSelection()
  if (!sel) return

  const text = wrapEquationForDocument(trimmed)
  if (!text) return

  applyRibbonTextEdit(ed, sel, text)
  ed.focus()
}

/** Prefill the Equation Editor from the current Monaco selection, if any. */
export function getEquationDraftFromEditor(ed: Ed | null | undefined): string | null {
  if (!ed) return null
  const selected = getSelectionText(ed).trim()
  if (!selected) return null
  return unwrapEquationDraft(selected) || null
}

export function insertCodeBlock(ed: Ed): void {
  const selected = getSelectionText(ed)
  if (selected) {
    const sel = ed.getSelection()
    if (!sel) return
    applyRibbonTextEdit(
      ed,
      sel,
      `\\begin{verbatim}\n${selected}\n\\end{verbatim}`,
    )
    ed.focus()
    return
  }
  insertSnippet(ed, "\\begin{verbatim}\n${1:code}\n\\end{verbatim}")
}

/**
 * Convert a native emoji (😀) to a twemojis hex key (1f600).
 * Multi-codepoint / ZWJ sequences become hyphen-separated (e.g. 1f468-200d-1f469).
 */
export function emojiToTwemojiKey(native: string): string {
  const parts: string[] = []
  for (const ch of native) {
    const cp = ch.codePointAt(0)
    if (cp == null) continue
    // Skip emoji-style variation selector; keep ZWJ / skin tones
    if (cp === 0xfe0f) continue
    parts.push(cp.toString(16))
  }
  return parts.join("-")
}

function resolveTwemojiKey(emojiOrKey: string): string {
  const raw = emojiOrKey.trim()
  if (!raw) return ""
  // Already a hex key from emoji-mart `unified` (e.g. "1f600" or "1f44b-1f3fd")
  if (/^[0-9a-f]{2,}(-[0-9a-f]+)*$/i.test(raw)) return raw.toLowerCase()
  return emojiToTwemojiKey(raw)
}

/**
 * Insert an emoji as a Twemoji PDF graphic via the `twemojis` package.
 * Raw Unicode emoji characters are dropped by pdfLaTeX/Tectonic; image-based
 * `\texttwemoji{…}` is portable and does not need system color-emoji fonts.
 */
export function insertEmoji(ed: Ed, emojiOrKey: string): void {
  const key = resolveTwemojiKey(emojiOrKey)
  if (!key) return
  ensurePackage(ed, "twemojis")
  const sel = ed.getSelection()
  if (!sel) return
  applyRibbonTextEdit(ed, sel, `\\texttwemoji{${key}}`)
  ed.focus()
}

export function insertTable(ed: Ed, rows = 3, cols = 3): void {
  const colSpec = "l".repeat(cols)
  const header = Array.from({ length: cols }, (_, i) => `\${${i + 1}:h${i + 1}}`).join(" & ")
  const emptyRow = Array.from({ length: cols }, () => " ").join(" & ")
  const body = Array.from({ length: Math.max(1, rows - 1) }, () => `  ${emptyRow} \\\\`).join("\n")
  insertSnippet(
    ed,
    `\\begin{tabular}{${colSpec}}\n  ${header} \\\\\n  \\hline\n${body}\n\\end{tabular}`,
  )
}

export function insertVideo(ed: Ed): void {
  insertSnippet(
    ed,
    "% Video (media9 / hyperlink)\n\\href{${1:https://example.com/video}}{\\includegraphics[width=0.6\\linewidth]{${2:thumbnail.jpg}}}",
  )
}

export function insertLink(ed: Ed): void {
  const selected = getSelectionText(ed)
  if (selected) {
    insertSnippet(
      ed,
      `\\href{\${1:https://example.com}}{${selected.replace(/\$/g, "\\$")}}`,
    )
    return
  }
  insertSnippet(ed, "\\href{${1:https://example.com}}{${2:link text}}")
}

export function insertBookmark(ed: Ed): void {
  insertSnippet(ed, "\\label{${1:bookmark-id}}")
}

export function insertComment(ed: Ed): void {
  const selected = getSelectionText(ed)
  if (selected) {
    wrapSelection(ed, "% ", "")
    return
  }
  insertSnippet(ed, "% ${1:comment}")
}

/**
 * Ensure fancyhdr is active in the preamble (never the body).
 * Page style + headheight belong before `\begin{document}` or headers/footers
 * often never appear (especially on page 1 / after `\maketitle`).
 */
function ensureFancyhdrSetup(ed: Ed): void {
  ensurePackage(ed, "fancyhdr")
  ensurePreambleCommand(ed, "\\pagestyle{fancy}")
  // Prevent Fancyhdr from clipping header text into the top margin
  ensurePreambleCommand(ed, "\\setlength{\\headheight}{15pt}")
  // Keep headers/footers on "plain" pages (title / page 1)
  ensurePreambleCommand(ed, "\\makeatletter\\let\\ps@plain\\ps@fancy\\makeatother")
}

/**
 * Upsert `\\fancyhead[C]{...}` / `\\fancyfoot[C]{...}` in the preamble.
 */
function setFancyHf(
  ed: Ed,
  which: "head" | "foot",
  text: string,
): void {
  ensureFancyhdrSetup(ed)
  const model = ed.getModel()
  if (!model) return

  // Re-read after preamble edits
  const full = model.getValue()
  const cmd = which === "head" ? "fancyhead" : "fancyfoot"
  const line = `\\${cmd}[C]{${text}}`
  const existing = new RegExp(
    String.raw`\\${cmd}\s*\[\s*C\s*\]\s*\{[^{}]*\}`,
  ).exec(full)

  if (existing && existing.index != null) {
    const start = model.getPositionAt(existing.index)
    const end = model.getPositionAt(existing.index + existing[0].length)
    const range = {
      startLineNumber: start.lineNumber,
      startColumn: start.column,
      endLineNumber: end.lineNumber,
      endColumn: end.column,
    }
    ed.executeEdits("ribbon-fancyhdr", [
      { range, text: line, forceMoveMarkers: true },
    ])
    highlightRibbonInsert(
      ed,
      existing.index,
      existing.index + line.length,
    )
  } else {
    ensurePreambleCommand(ed, line)
  }

  // Light rule so the band is visible even with short text
  if (which === "head") {
    ensurePreambleCommand(ed, "\\renewcommand{\\headrulewidth}{0.4pt}")
  } else {
    ensurePreambleCommand(ed, "\\renewcommand{\\footrulewidth}{0.4pt}")
  }

  ed.focus()
}

export function insertHeader(ed: Ed): void {
  setFancyHf(ed, "head", "Header text")
}

export function insertFooter(ed: Ed): void {
  setFancyHf(ed, "foot", "Footer text")
}

export function insertPageNumber(ed: Ed): void {
  insertSnippet(ed, "\\thepage")
}

export function insertTextBox(ed: Ed): void {
  insertSnippet(
    ed,
    "\\fbox{\\parbox{0.45\\linewidth}{\${1:Text box content}}}",
  )
}

export function insertDateAndTime(ed: Ed): void {
  insertSnippet(ed, "\\today")
}

export function insertDropCap(ed: Ed): void {
  ensurePackage(ed, "lettrine")
  insertSnippet(
    ed,
    `\\lettrine{\${1:A}}{\${2:fter}} \${3:the drop cap text continues here.}`,
  )
}

export function insertDraw(ed: Ed): void {
  ensurePackage(ed, "tikz")
  insertSnippet(
    ed,
    `\\begin{tikzpicture}\n  \\draw[thick, ->] (0,0) -- (2,1);\n  \\node at (1,1.3) {\${1:label}};\n\\end{tikzpicture}`,
  )
}

/**
 * Ensure a preamble command/line exists (e.g. `\onehalfspacing`).
 * Inserts before `\begin{document}` if missing; no-op if already present.
 */
function ensurePreambleCommand(ed: Ed, commandLine: string): void {
  const model = ed.getModel()
  if (!model) return

  const full = model.getValue()
  const needle = commandLine.trim()
  if (!needle) return

  // Already present as its own command (ignore leading backslash for escape)
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  if (new RegExp(escaped).test(full)) return

  const line = needle.endsWith("\n") ? needle : `${needle}\n`

  const beginMatch = /\\begin\s*\{\s*document\s*\}/.exec(full)
  if (beginMatch && beginMatch.index != null) {
    insertTextAtOffset(ed, beginMatch.index, line)
    return
  }

  // After \documentclass if no \begin{document}
  const docMatch = /\\documentclass\s*(?:\[[^\]]*\])?\s*\{[^}]+\}/.exec(full)
  if (docMatch && docMatch.index != null) {
    let at = docMatch.index + docMatch[0].length
    if (full[at] === "\r") at++
    if (full[at] === "\n") at++
    insertTextAtOffset(ed, at, line)
    return
  }

  insertTextAtOffset(ed, 0, line)
}

function insertTextAtOffset(ed: Ed, offset: number, text: string): void {
  const model = ed.getModel()
  if (!model) return
  const pos = model.getPositionAt(offset)
  ed.executeEdits("ribbon-preamble", [
    {
      range: {
        startLineNumber: pos.lineNumber,
        startColumn: pos.column,
        endLineNumber: pos.lineNumber,
        endColumn: pos.column,
      },
      text,
      forceMoveMarkers: true,
    },
  ])
  highlightRibbonInsert(ed, offset, offset + text.length)
}

export function insertDocumentFormatting(ed: Ed): void {
  const model = ed.getModel()
  if (!model) return

  const full = model.getValue()
  const hasDocClass = /\\documentclass\s*(?:\[[^\]]*\])?\s*\{[^}]+\}/.test(full)

  // No document class yet — insert a full preamble block (never into the body).
  if (!hasDocClass) {
    const block =
      `% Document theme / formatting\n` +
      `\\documentclass[11pt,a4paper]{article}\n` +
      `\\usepackage[margin=1in]{geometry}\n` +
      `\\usepackage{setspace}\n` +
      `\\onehalfspacing\n`

    const beginMatch = /\\begin\s*\{\s*document\s*\}/.exec(full)
    if (beginMatch && beginMatch.index != null) {
      insertTextAtOffset(ed, beginMatch.index, block)
    } else {
      insertTextAtOffset(ed, 0, block)
    }
    ed.focus()
    return
  }

  // Document already has a class — only add missing preamble packages / settings.
  // Do not insert another \documentclass (illegal / wrong in the body).
  ensurePackage(ed, "geometry", "margin=1in")
  ensurePackage(ed, "setspace")
  ensurePreambleCommand(ed, "\\onehalfspacing")
  ed.focus()
}

/**
 * Ensure `\definecolor{name}{model}{value}` exists in the preamble.
 * Replaces an existing definition for the same name; otherwise inserts before
 * `\begin{document}` (or after `\documentclass`).
 */
function ensureDefineColor(
  ed: Ed,
  name: string,
  colorModel: string,
  value: string,
): void {
  const model = ed.getModel()
  if (!model) return

  const full = model.getValue()
  const line = `\\definecolor{${name}}{${colorModel}}{${value}}`
  const existing = new RegExp(
    String.raw`\\definecolor\s*\{\s*${name}\s*\}\s*\{[^}]*\}\s*\{[^}]*\}`,
  ).exec(full)

  if (existing && existing.index != null) {
    const start = model.getPositionAt(existing.index)
    const end = model.getPositionAt(existing.index + existing[0].length)
    ed.executeEdits("ribbon-definecolor", [
      {
        range: {
          startLineNumber: start.lineNumber,
          startColumn: start.column,
          endLineNumber: end.lineNumber,
          endColumn: end.column,
        },
        text: line,
        forceMoveMarkers: true,
      },
    ])
    highlightRibbonInsert(ed, existing.index, existing.index + line.length)
    return
  }

  const text = `${line}\n`
  const beginMatch = /\\begin\s*\{\s*document\s*\}/.exec(full)
  if (beginMatch && beginMatch.index != null) {
    insertTextAtOffset(ed, beginMatch.index, text)
    return
  }

  const docMatch = /\\documentclass\s*(?:\[[^\]]*\])?\s*\{[^}]+\}/.exec(full)
  if (docMatch && docMatch.index != null) {
    let at = docMatch.index + docMatch[0].length
    if (full[at] === "\r") at++
    if (full[at] === "\n") at++
    insertTextAtOffset(ed, at, text)
    return
  }

  insertTextAtOffset(ed, 0, text)
}

/**
 * Define a named theme accent color in the preamble, then optionally apply it
 * to the current selection via `\textcolor{themeaccent}{…}`.
 * Does not insert a global `\color{…}` (that would recolor the rest of the body).
 */
export function insertThemeColor(ed: Ed, color = "#2563EB"): void {
  ensurePackage(ed, "xcolor")
  const hex = hexToXcolor(color)
  ensureDefineColor(ed, "themeaccent", "HTML", hex)

  const selected = getSelectionText(ed)
  if (selected) {
    wrapSelection(ed, "\\textcolor{themeaccent}{", "}")
  }
  ed.focus()
}

export function insertFontTheme(ed: Ed, fontName = "TeX Gyre Termes"): void {
  applyFontFamily(ed, fontName)
}

export function insertEffects(ed: Ed): void {
  ensurePackage(ed, "soul")
  wrapSelection(ed, "\\hl{", "}", "highlighted text")
}

export function insertWatermark(ed: Ed): void {
  ensurePackage(ed, "draftwatermark")
  ensurePreambleCommand(ed, "\\SetWatermarkText{DRAFT}")
  ensurePreambleCommand(ed, "\\SetWatermarkScale{1}")
  ed.focus()
}

export function applyPageColor(ed: Ed, color: string): void {
  ensurePackage(ed, "xcolor")
  const hex = hexToXcolor(color)
  insertSnippet(ed, `\\pagecolor[HTML]{${hex}}\n`)
}

export function insertPageEffects(ed: Ed): void {
  ensurePackage(ed, "fancybox")
  insertSnippet(ed, "\\shadowbox{${1:content}}")
}

export function insertPageBorder(ed: Ed): void {
  ensurePackage(ed, "crop", "a4,frame")
  ed.focus()
}

// ─── Layout ───────────────────────────────────────────────────────────────────

/**
 * Set or update a geometry key in the preamble (`\geometry{key=value}`).
 * Ensures `\usepackage{geometry}` exists; never inserts into the document body.
 */
function setGeometryOption(ed: Ed, key: string, value: string): void {
  ensurePackage(ed, "geometry")
  const model = ed.getModel()
  if (!model) return

  // Re-read after ensurePackage may have edited the buffer
  const full = model.getValue()
  const keyRe = new RegExp(String.raw`(^|,)(\s*)${key}\s*=\s*[^,}\s]+`)
  const geoRe = /\\geometry\s*\{([^}]*)\}/g
  let match: RegExpExecArray | null
  let updateTarget: RegExpExecArray | null = null
  let firstGeo: RegExpExecArray | null = null
  while ((match = geoRe.exec(full)) !== null) {
    if (!firstGeo) firstGeo = match
    if (keyRe.test(match[1])) {
      updateTarget = match
      break
    }
  }

  const replaceGeometry = (target: RegExpExecArray, inner: string) => {
    const replacement = `\\geometry{${inner}}`
    const start = model.getPositionAt(target.index!)
    const end = model.getPositionAt(target.index! + target[0].length)
    ed.executeEdits("ribbon-geometry", [
      {
        range: {
          startLineNumber: start.lineNumber,
          startColumn: start.column,
          endLineNumber: end.lineNumber,
          endColumn: end.column,
        },
        text: replacement,
        forceMoveMarkers: true,
      },
    ])
    highlightRibbonInsert(
      ed,
      target.index!,
      target.index! + replacement.length,
    )
  }

  if (updateTarget) {
    replaceGeometry(
      updateTarget,
      updateTarget[1].replace(keyRe, `$1$2${key}=${value}`),
    )
    return
  }

  // Also treat \usepackage[...margin=...]{geometry} as already covering "margin"
  if (
    key === "margin" &&
    /\\usepackage\s*\[[^\]]*margin\s*=/.test(full)
  ) {
    return
  }

  // Append to an existing \geometry{...} when present
  if (firstGeo) {
    const trimmed = firstGeo[1].trim()
    const inner = trimmed ? `${trimmed},${key}=${value}` : `${key}=${value}`
    replaceGeometry(firstGeo, inner)
    return
  }

  ensurePreambleCommand(ed, `\\geometry{${key}=${value}}`)
}

export function insertMargins(ed: Ed): void {
  // Prefer loading geometry with a default margin; if geometry is already loaded,
  // setGeometryOption adds/updates `\geometry{margin=1in}` in the preamble.
  ensurePackage(ed, "geometry", "margin=1in")
  setGeometryOption(ed, "margin", "1in")
  ed.focus()
}

export function applyMarginSide(
  ed: Ed,
  side: "left" | "right" | "top" | "bottom",
  valuePx: number,
): void {
  // geometry accepts absolute units; treat the ribbon input as pt for layout
  const val = `${Math.max(0, valuePx)}pt`
  setGeometryOption(ed, side, val)
  ed.focus()
}

export function insertOrientation(ed: Ed, landscape = true): void {
  if (landscape) {
    insertSnippet(
      ed,
      `% Landscape page\n\\usepackage[landscape]{geometry}\n`,
    )
  } else {
    insertSnippet(
      ed,
      `% Portrait page\n\\usepackage[portrait]{geometry}\n`,
    )
  }
}

export function insertPageSize(ed: Ed): void {
  insertSnippet(
    ed,
    `% Page size\n\\usepackage[a4paper]{geometry}\n% Alternatives: letterpaper, a5paper, legalpaper\n`,
  )
}

export function insertColumns(ed: Ed, count = 2): void {
  ensurePackage(ed, "multicol")
  const selected = getSelectionText(ed)
  if (selected) {
    const sel = ed.getSelection()
    if (!sel) return
    applyRibbonTextEdit(
      ed,
      sel,
      `\\begin{multicols}{${count}}\n${selected}\n\\end{multicols}`,
    )
    ed.focus()
    return
  }
  insertSnippet(
    ed,
    `\\begin{multicols}{${count}}\n  \${1:column content}\n\\end{multicols}`,
  )
}

export function insertBreaks(ed: Ed): void {
  insertSnippet(
    ed,
    `% Page / section break\n\\newpage\n% or: \\pagebreak  \\clearpage  \\newpage\n`,
  )
}

export function insertLineNumbers(ed: Ed): void {
  insertSnippet(
    ed,
    `% Requires \\usepackage{lineno}\n\\usepackage{lineno}\n\\linenumbers\n`,
  )
}

export function insertPosition(ed: Ed): void {
  ensurePackage(ed, "tikz")
  insertSnippet(
    ed,
    `% Object position (absolute)\n\\begin{tikzpicture}[remember picture,overlay]\n  \\node[anchor=north west] at (current page.north west)\n    [xshift=\${1:2cm},yshift=\${2:-2cm}] {\${3:content}};\n\\end{tikzpicture}`,
  )
}

export function insertSelectPanel(ed: Ed): void {
  insertSnippet(
    ed,
    `% Selection / label marker\n\\label{\${1:object-id}}\n`,
  )
}

export function insertWrapText(ed: Ed): void {
  ensurePackage(ed, "wrapfig")
  ensurePackage(ed, "graphicx")
  insertSnippet(
    ed,
    `% Text wrap\n\\begin{wrapfigure}{\${1:r}}{0.4\\textwidth}\n  \\centering\n  \\includegraphics[width=0.38\\textwidth]{\${2:assets/hello_world.png}}\n  \\caption{\${3:Caption}}\n\\end{wrapfigure}`,
  )
}

export function insertBringForward(ed: Ed): void {
  insertSnippet(
    ed,
    `% Bring forward (draw last / on top in TikZ)\n\\begin{tikzpicture}\n  \\draw[fill=gray!20] (0,0) rectangle (2,1.5);\n  \\draw[fill=blue!40] (0.5,0.3) rectangle (2.5,1.8); % on top\n\\end{tikzpicture}`,
  )
}

export function insertBringBackward(ed: Ed): void {
  insertSnippet(
    ed,
    `% Send backward (draw first / behind in TikZ)\n\\begin{tikzpicture}\n  \\draw[fill=blue!40] (0.5,0.3) rectangle (2.5,1.8); % behind\n  \\draw[fill=gray!20] (0,0) rectangle (2,1.5);\n\\end{tikzpicture}`,
  )
}

export function insertObjectAlign(ed: Ed): void {
  insertSnippet(
    ed,
    `% Align object (center on page)\n\\begin{center}\n  \${1:content}\n\\end{center}`,
  )
}

// ─── Mailings ─────────────────────────────────────────────────────────────────

export function insertEnvelopes(ed: Ed): void {
  insertSnippet(
    ed,
    `% Envelope (geometry)\n\\documentclass{letter}\n\\usepackage[paperwidth=9.5in,paperheight=4.125in]{geometry}\n\\begin{document}\n\\textbf{\${1:Recipient Name}}\\\\\n\${2:Address line 1}\\\\\n\${3:City, State ZIP}\n\\end{document}`,
  )
}

export function insertLabels(ed: Ed): void {
  insertSnippet(
    ed,
    `% Requires \\usepackage{labels} or glossaries\n\\usepackage[newdimens]{labels}\n\\LabelCols=2\n\\LabelRows=5\n\\addresslabel{\n  \\textbf{\${1:Name}}\\\\\n  \${2:Address}\n}`,
  )
}

export function insertMailMerge(ed: Ed): void {
  insertSnippet(
    ed,
    `% Mail merge fields (datatool / csvsimple)\n% Requires \\usepackage{datatool}\n\\DTLloaddb{recipients}{\${1:recipients.csv}}\n\\DTLforeach{recipients}{\\First=first,\\Last=last,\\Email=email}{%\n  Dear \\First~\\Last,\\\\\n  % body\n  \\newpage\n}`,
  )
}

export function insertSelectRecipients(ed: Ed): void {
  insertSnippet(
    ed,
    `% Recipients data source\n% \\DTLloaddb{recipients}{recipients.csv}\n% Columns: first, last, email, address\n`,
  )
}

export function insertEditRecipients(ed: Ed): void {
  insertSnippet(
    ed,
    `% Edit recipient list (CSV)\n% first,last,email,address\n% Jane,Doe,jane@example.com,"123 Main St"\n`,
  )
}

export function insertHighlightFields(ed: Ed): void {
  ensurePackage(ed, "xcolor")
  insertSnippet(
    ed,
    `\\textcolor{blue}{\\texttt{<<\${1:FieldName}>>}}\n`,
  )
}

export function insertAddressBlock(ed: Ed): void {
  insertSnippet(
    ed,
    `\\textbf{\\First~\\Last}\\\\\n\\Address\\\\\n\\City, \\State~\\Zip\n`,
  )
}

export function insertGreetingLine(ed: Ed): void {
  insertSnippet(ed, `Dear \\First~\\Last,\n\n`)
}

export function insertMergeField(ed: Ed): void {
  insertSnippet(ed, `<<\${1:FieldName}>>`)
}

export function insertMatchFields(ed: Ed): void {
  insertSnippet(
    ed,
    `% Match merge fields\n% \\First  <- first\n% \\Last   <- last\n% \\Email  <- email\n% \\Address <- address\n`,
  )
}

export function insertRules(ed: Ed): void {
  insertSnippet(
    ed,
    `% Conditional merge rule\n\\DTLifnullorempty{\\Email}{%\n  % no email\n}{%\n  Email: \\Email\n}\n`,
  )
}

export function insertUpdateLabel(ed: Ed): void {
  insertSnippet(
    ed,
    `% Update labels from recipient database\n\\DTLforeach{recipients}{\\First=first,\\Last=last,\\Address=address}{%\n  \\addresslabel{\\textbf{\\First~\\Last}\\\\\\Address}\n}\n`,
  )
}

export function insertPreviewResults(ed: Ed): void {
  insertSnippet(
    ed,
    `% Preview merge result for record \${1:1}\n\\DTLassign{recipients}{\${1:1}}{\\First=first,\\Last=last}\nDear \\First~\\Last,\n`,
  )
}

export function insertCheckForErrors(ed: Ed): void {
  insertSnippet(
    ed,
    `% Check merge fields exist\n\\DTLforeach{recipients}{\\First=first,\\Last=last}{%\n  \\DTLifnullorempty{\\First}{\\PackageWarning{mailmerge}{Missing first name}}{}\n}\n`,
  )
}

export function findRecipientInEditor(ed: Ed, query: string): void {
  if (!query.trim()) return
  ed.focus()
  ed.getAction("actions.find")?.run()
  // Fallback: select first occurrence
  const model = ed.getModel()
  if (!model) return
  const matches = model.findMatches(query, false, false, false, null, true)
  if (matches.length > 0) {
    ed.setSelection(matches[0].range)
    ed.revealRangeInCenter(matches[0].range)
  }
}

/** Hex → approximate named / HTML color for xcolor */
export function hexToXcolor(hex: string): string {
  const clean = hex.replace("#", "").toUpperCase()
  if (/^[0-9A-F]{6}$/.test(clean)) return clean
  if (/^[0-9A-F]{3}$/.test(clean)) {
    return clean
      .split("")
      .map((c) => c + c)
      .join("")
  }
  return "000000"
}
//Test